import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SuggestionCard from "@/components/hfacs/SuggestionCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CopilotTab({ analysisId, isImported }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hfacs/${analysisId}/suggestions`);
      setSuggestions(res.data);
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  useEffect(() => { load(); }, [load]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      await axios.post(`${API}/hfacs/${analysisId}/ai-analysis`);
      toast.success("Analyse du Copilote terminée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'analyse.");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div>
      <div className="mb-4 px-4 py-3 rounded-sm border border-cyan-500/30 bg-cyan-500/5 text-xs text-cyan-700">
        Le Copilote suggère des pistes à examiner à partir des faits relevés. Il ne détermine jamais une cause
        et ne remplace pas le jugement de l'équipe.
        {isImported && (
          <span data-testid="copilot-import-hint" className="block mt-2 text-amber-700">
            Analyse importée : le Copilote ne proposera que des facteurs qui n'ont pas déjà été identifiés dans l'étude d'origine — utile pour vérifier que rien n'a été oublié.
          </span>
        )}
      </div>
      <Button onClick={runAnalysis} disabled={analyzing} data-testid="copilot-analyze-button" className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2 mb-6">
        {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        Analyser les faits avec le Copilote HFACS
      </Button>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="suggestions-grid">
          {suggestions.map((s) => (
            <SuggestionCard key={s.ID_SUGGESTION} analysisId={analysisId} s={s} onDecided={load} />
          ))}
          {suggestions.length === 0 && (
            <p className="text-sm text-muted-foreground">Aucune suggestion pour le moment.</p>
          )}
        </div>
      )}
    </div>
  );
}
